import { translate } from '@jsverse/transloco';

/**
 * Croatian defaults for the campaign promo strings — free days and bonus days (offer card, plan badge, summary,
 * redeemed/upcoming badges, cancel-modal messages and the payment-return copy).
 * Translations come from the backend (/api/v1/localization) and these keys are new
 * there — until they are added a missing key would render raw, so the default stands
 * in. Keep the keys in sync with what is handed to the BE for localization.
 */
const DEFAULTS: Record<string, string> = {
    // Header pill + toast — the promo variant of the card-confirmation notice
    'payment.promo.header.pill': '{0} dana besplatno',
    'payment.promo.header.action': 'Aktiviraj',
    'payment.promo.toast.eyebrow': 'Posebna ponuda',
    'payment.promo.toast.title': 'Imaš {0} dana besplatno',
    'payment.promo.toast.text': 'Aktiviraj besplatne dane u Mom računu: potvrdi karticu i gledaj besplatno, a prva naplata stiže tek nakon isteka besplatnog razdoblja.',
    // Bonus days: a running subscription confirms the card (charged on the expiry day), an expired
    // one buys now. Toast-only keys, so the copy can be tuned per operator without touching the
    // offer card, the plan summary or the return page.
    'payment.promo.toast.text_bonus_renewal': 'Potvrdi karticu u Mom računu: pretplata se obnavlja na dan isteka, a nakon plaćenog razdoblja dobivaš još {0} dana besplatno.',
    'payment.promo.toast.text_bonus_buy': 'Kupi pretplatu i nakon plaćenog razdoblja dobivaš još {0} dana besplatno.',
    'payment.promo.toast.confirm': 'Pogledaj ponudu',
    // Offer card — active-subscription-details
    'payment.promo.offer.title': '{0} dana besplatno',
    'payment.promo.offer.period': 'Besplatno razdoblje: {0} – {1}',
    'payment.promo.offer.first_charge': 'Prva naplata: {0} · {1}',
    'payment.promo.offer.reservation': 'Radi provjere kartice rezervirat ćemo {0} i rezervaciju odmah poništiti.',
    'payment.promo.offer.cancel_rule': 'Otkaz u besplatnom razdoblju odmah gasi pristup, naplate nema.',
    'payment.promo.offer.activate': 'Aktiviraj',
    // Plan picker — subscription-plans
    'payment.promo.plan.badge': '{0} dana besplatno',
    'payment.promo.summary.period': 'Besplatno razdoblje',
    'payment.promo.summary.first_charge': 'Prva naplata',
    'payment.promo.summary.today': 'Danas se ne naplaćuje ništa (rezervacija {0}).',
    // POST payments/checkout answered 409: a checkout for this offering is already in flight.
    'payment.promo.checkout_in_progress': 'Plaćanje je već u tijeku. Pričekaj nekoliko minuta i pokušaj ponovno.',
    // Redeemed / upcoming badges — subscription card
    'payment.promo.badge.free_until': 'Besplatno do {0}',
    // {1} = freeTo; a value without it still reads "Besplatni dani od 6.12.".
    'payment.promo.badge.free_from': 'Besplatni dani od {0} do {1}',
    'payment.promo.label.first_charge': 'Prva naplata',
    // Cancel modal — replaces the default cancel message while a promo applies
    'payment.promo.cancel_message_free_period': 'Ako sada prekineš pretplatu, pristup prestaje odmah i ništa ti nećemo naplatiti. Želiš li nastaviti?',
    'payment.promo.cancel_message_before_free_period': 'Pretplata vrijedi do kraja plaćenog razdoblja, a besplatni dani se neće dodijeliti. Želiš li nastaviti?',
    // Payment return
    'payment.promo.success.title': 'Besplatni dani su aktivirani',
    'payment.promo.success.message': 'Gledaš besplatno do {0}. Prva naplata je {1}, a obnovu možeš isključiti u Mom računu.',
    'payment.promo.failed.title': 'Aktivacija nije uspjela',
    'payment.promo.failed.message': 'Kartica nije potvrđena. Ništa nije naplaćeno — pokušaj ponovno.',
    // Bonus days — pay for the period first, the free days follow it. No dates from the BE.
    // `free_days` is also the toast text and the return message when the purchase is charged now.
    'payment.promo.bonus.free_days': 'Nakon plaćenog razdoblja dobivaš još {0} dana besplatno.',
    'payment.promo.bonus.offer.charge': 'Naplata na dan isteka pretplate: {0} · {1}',
    // Cancelling, in the paid or the free period, keeps the bonus days (flow 19.10.).
    'payment.promo.bonus.cancel_rule': 'Ukoliko otkažeš, pristup traje do kraja besplatnog razdoblja, a sljedeća naplata se ne izvršava.',
    'payment.promo.bonus.success.title': 'Ponuda je aktivirana',
    // Subscriptions page once a bonus offer is taken (customer/subscriptions `bonusDays`); the text
    // reuses `bonus.success.message_renewal`.
    'payment.promo.bonus.active.title': 'Ponuda je aktivirana · kartica potvrđena',
    'payment.promo.bonus.active.badge': '+{0} dana gratis',
    'payment.promo.bonus.success.message_renewal': 'Pretplata se obnavlja {0}, a nakon plaćenog razdoblja dobivaš još {1} dana besplatno.',
};

/** `translate` with the Croatian default when the backend has no value for the key yet. */
export function promoText(key: string, params: string[] = []): string {
    const translated = translate(key, Object.fromEntries(params.map((p, i) => [i, p])));
    if (translated && translated !== key) return translated;
    return (DEFAULTS[key] ?? key).replace(/\{(\d+)\}/g, (match, i) => params[+i] ?? match);
}
